import { supabase } from "@/integrations/supabase/client";
import { withSilentRetry, isRetryableError } from "./supabase-retry";

export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_DELAY_MS = 30000; // 30 segundos entre tentativas

export interface CentralizedPrintJob {
  id: string;
  restaurant_id: string;
  order_number: number;
  printer_name: string;
  content: string;
  location_name: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  printed_at: string | null;
  error_message: string | null;
  attempts: number;
}

/**
 * Saves print jobs to the database for centralized processing.
 * Jobs will be picked up by any device running QZ Tray (usually the PC).
 */
/**
 * Encodes content to Base64 to safely store ESC/POS control characters in PostgreSQL.
 */
function encodeContentForDatabase(content: string): string {
  try {
    return btoa(unescape(encodeURIComponent(content)));
  } catch (e) {
    console.error("[PRINT-QUEUE] Error encoding content:", e);
    return content;
  }
}

/**
 * Saves print jobs to the database for centralized processing.
 * Jobs will be picked up by any device running QZ Tray (usually the PC).
 * Content is Base64 encoded to handle ESC/POS control characters.
 */
export async function savePrintJobsToDatabase(
  restaurantId: string,
  jobs: {
    orderNumber: number;
    printerName: string;
    content: string;
    locationName?: string;
  }[]
): Promise<boolean> {
  if (jobs.length === 0) return true;

  const result = await withSilentRetry(async () => {
    const { error } = await supabase.from("pending_print_jobs").insert(
      jobs.map((job) => ({
        restaurant_id: restaurantId,
        order_number: job.orderNumber,
        printer_name: job.printerName,
        content: encodeContentForDatabase(job.content),
        location_name: job.locationName || null,
        status: "pending",
      }))
    );

    if (error) throw error;
    return true;
  });

  if (result) {
    console.log(`[PRINT-QUEUE] ${jobs.length} job(s) saved to database (Base64 encoded)`);
    return true;
  }

  console.error("[PRINT-QUEUE] Failed to save jobs after retries");
  return false;
}

/**
 * Fetches pending print jobs from the database.
 * Handles network errors silently (will retry on next interval).
 */
export async function fetchPendingPrintJobs(
  restaurantId: string
): Promise<CentralizedPrintJob[]> {
  const result = await withSilentRetry(async () => {
    const { data, error } = await supabase
      .from("pending_print_jobs")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .eq("status", "pending")
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data || []) as CentralizedPrintJob[];
  }, { maxRetries: 2 }); // Fewer retries for polling queries

  return result || [];
}

/**
 * Marks a print job as completed.
 * Returns true if the database update succeeded, false otherwise.
 * Does NOT swallow errors — caller must handle failures.
 */
/**
 * Atomically claims a job for processing by setting status to 'printing'.
 * Returns true if this instance successfully claimed it (1 row updated),
 * false if another instance already claimed it (0 rows updated).
 */
export async function claimJobForProcessing(jobId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("pending_print_jobs")
      .update({ status: "printing", updated_at: new Date().toISOString() })
      .eq("id", jobId)
      .eq("status", "pending")
      .select("id");

    if (error) {
      console.error("[PRINT-QUEUE] Failed to claim job:", error.message);
      return false;
    }

    const claimed = (data?.length ?? 0) > 0;
    if (!claimed) {
      console.log(`[PRINT-QUEUE] Job ${jobId} already claimed by another instance, skipping`);
    }
    return claimed;
  } catch (e) {
    console.error("[PRINT-QUEUE] Network error claiming job:", e);
    return false;
  }
}

export async function markJobAsPrinted(jobId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("pending_print_jobs")
      .update({
        status: "printed",
        printed_at: new Date().toISOString(),
      })
      .eq("id", jobId)
      .eq("status", "printing");

    if (error) {
      console.error("[PRINT-QUEUE] Failed to mark job as printed:", error.message);
      return false;
    }
    return true;
  } catch (e) {
    console.error("[PRINT-QUEUE] Network error marking job as printed:", e);
    return false;
  }
}

/**
 * Marks a print job as failed.
 */
export async function markJobAsFailed(
  jobId: string,
  errorMessage: string
): Promise<void> {
  await withSilentRetry(async () => {
    const { error } = await supabase
      .from("pending_print_jobs")
      .update({
        status: "failed",
        error_message: errorMessage,
      })
      .eq("id", jobId)
      .eq("status", "printing");

    if (error) throw error;
  });
}

/**
 * Increments job attempt counter. Returns true if will retry, false if max attempts reached.
 */
export async function incrementJobAttempt(
  jobId: string,
  errorMessage: string
): Promise<boolean> {
  // Buscar job atual para ver quantas tentativas já teve
  const { data: job } = await supabase
    .from("pending_print_jobs")
    .select("attempts")
    .eq("id", jobId)
    .single();

  const currentAttempts = ((job?.attempts as number) || 0) + 1;

  if (currentAttempts >= MAX_RETRY_ATTEMPTS) {
    // Limite atingido - marca como failed definitivamente
    await markJobAsFailed(jobId, `${errorMessage} (após ${currentAttempts} tentativas)`);
    console.log(`[PRINT-QUEUE] Job ${jobId} falhou definitivamente após ${currentAttempts} tentativas`);
    return false;
  }

  // Atualiza tentativas e reseta status para pending para retry
  const { error } = await supabase
    .from("pending_print_jobs")
    .update({
      status: "pending",
      attempts: currentAttempts,
      error_message: `Tentativa ${currentAttempts}/${MAX_RETRY_ATTEMPTS}: ${errorMessage}`,
      updated_at: new Date().toISOString(),
    })
    .eq("id", jobId);

  if (error) {
    console.error("[PRINT-QUEUE] Error incrementing job attempt:", error);
    return false;
  }

  console.log(`[PRINT-QUEUE] Job ${jobId} tentativa ${currentAttempts}/${MAX_RETRY_ATTEMPTS}, retry em ${RETRY_DELAY_MS / 1000}s`);
  return true;
}

/**
 * Deletes old completed/failed jobs (older than 24 hours).
 */
export async function cleanupOldPrintJobs(restaurantId: string): Promise<void> {
  const oneDayAgo = new Date();
  oneDayAgo.setHours(oneDayAgo.getHours() - 24);

  const { error } = await supabase
    .from("pending_print_jobs")
    .delete()
    .eq("restaurant_id", restaurantId)
    .in("status", ["printed", "failed"])
    .lt("created_at", oneDayAgo.toISOString());

  if (error) {
    console.error("[PRINT-QUEUE] Error cleaning up old jobs:", error);
  }
}
